import { createSelector, Selector } from '@reduxjs/toolkit'
import { Tenant, User, CustomFieldDefinition, CustomFieldUsage } from '@alucio/aws-beacon-amplify/src/models'
import { selTenantMap, tenantCustomFieldsFactory } from 'src/state/redux/selector/tenant'
import { RootState } from 'src/state/redux'
import { loggedUser } from 'src/state/redux/selector/user'
import { FeatureFlags, FeatureFlag, FeatureFlagKeys, FeatureFlagConsts } from 'src/types/featureFlags'
import type { LDClient } from 'launchdarkly-react-client-sdk'
import loggers from 'src/utils/logger'
import { useSelector } from 'react-redux'

const LOCAL_STORAGE_KEY = 'debug_feature_flags_override';
let OVERRIDES_ENABLED: boolean | undefined = undefined

function getDebugOverride(alias: FeatureFlagKeys): boolean | string | number | undefined {
  // [NOTE] - Optimization to avoid checking localstorage when debugging is not enabled
  if (OVERRIDES_ENABLED === false)
    return undefined

  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);

    if (OVERRIDES_ENABLED === undefined) {
      loggers.debug.info("Enabling permissions override debugging")
      OVERRIDES_ENABLED = true
    }

    if (stored) {
      const overrides = JSON.parse(stored);
      if (alias in overrides) {
        return overrides[alias];
      }
    }
    else {
      OVERRIDES_ENABLED = false
    }
  } catch {
    // Ignore localStorage errors
  }

  return undefined;
}

export function getFeatureFlagValue<T extends FeatureFlagKeys>(
  alias: T,
  LDPermissions: FeatureFlagConsts,
  currentTenant?: Tenant,
  userRole?: string | null,
  currentUser?: User,
  userFilterFields?: CustomFieldDefinition[],
): typeof FeatureFlags[T]['defaultValue'];
export function getFeatureFlagValue(
  alias: FeatureFlagKeys,
  LDPermissions: FeatureFlagConsts,
  currentTenant?: Tenant,
  userRole?: string | null,
  currentUser?: User,
  userFilterFields?: CustomFieldDefinition[],
): boolean | string | number {
  const currentFlag: FeatureFlag = FeatureFlags[alias]
  const { flag, defaultValue, ALLOWED_ROLES } = currentFlag

  // 0. Check localStorage override (DEBUG) - highest priority
  const debugOverride = getDebugOverride(alias);
  if (debugOverride !== undefined) {
    return debugOverride as boolean | string;
  }

  // 1. Check if user role is allowed (if ALLOWED_ROLES is defined)
  if (ALLOWED_ROLES && userRole && !ALLOWED_ROLES.some((role) => role === userRole)) {
    return false;
  }

  // 2. Check if disabled by user's permission labels stored in lockedFiltersCustomValues
  if (currentUser?.lockedFiltersCustomValues && userFilterFields) {
    for (const customValue of currentUser.lockedFiltersCustomValues) {
      const fieldDef = userFilterFields.find(f => f.id === customValue.fieldId);
      if (fieldDef) {
        for (const valueId of customValue.values) {
          const valueDef = fieldDef.fieldValueDefinitions.find(v => v.id === valueId);
          const disabledFlags = valueDef?.disabledUserPermissions;
          // Compare against the flag name (e.g., 'enable_folder_management') which is stored in DB
          if (disabledFlags?.includes(flag)) {
            return false;
          }
        }
      }
    }
  }

  // 3. Tenant config takes precedence
  // [TODO-TS] - This type can probably be unwrapped from FeatureFlagConsts
  let configValue: string | number | boolean | undefined = currentTenant
    ?.featureFlags
    ?.find(tenantFlag => tenantFlag.flagId === flag)
    ?.value

  // 4. Fallback to LD value / hardcoded value initially synced during hydration
  if (typeof configValue !== 'boolean' && typeof configValue !== 'string') {
    configValue = LDPermissions[alias]
  }

  return configValue ?? defaultValue
}

export const initLDFeatureFlagValues = (LDClient: LDClient): FeatureFlagConsts => {
  const ldFlags = Object
    .entries(FeatureFlags)
    .reduce<FeatureFlagConsts>(
      (acc, [alias, flagDef]) => ({
        ...acc,
        [alias]: LDClient?.variation(flagDef.flag, flagDef.defaultValue)
      }),
      {} as FeatureFlagConsts,
    );

  return ldFlags
}

const tenantCustomFieldsInstance = tenantCustomFieldsFactory()
const userFilterQuery = { usages: { internalUsages: [CustomFieldUsage.USER_FILTER] } }

/**
 * This is an instance of a selector with static arguments for filtering
 */
export const userPermissionsCustomFields: Selector<RootState, CustomFieldDefinition[]> = createSelector(
  (state) => tenantCustomFieldsInstance(
    state,
    undefined,
    userFilterQuery    
  ),
  (userFields) => userFields
)

export const selPermissions = (state: RootState): FeatureFlagConsts => state.permissions.records[0]

export const selCurrentUserPermissions: Selector<RootState, FeatureFlagConsts> = createSelector(
  selPermissions,
  loggedUser,
  selTenantMap,
  userPermissionsCustomFields,
  (permissions, currentUser, tenantsMap, userPermissionsCustomFields) => {
    const userTenant = tenantsMap[currentUser?.userProfile?.tenantId ?? '-1']
    const userRecord = currentUser.userProfile
    const userRole = currentUser.userProfile?.role

    const computedFlags = Object
      .keys(FeatureFlags)
      .reduce<FeatureFlagConsts>(
        (acc, alias) => ({
          ...acc,
          [alias]: getFeatureFlagValue(
            alias as FeatureFlagKeys,
            permissions ?? {},
            userTenant,
            userRole,
            userRecord,
            userPermissionsCustomFields
          ),
        }),
        {} as FeatureFlagConsts,
      );

    return computedFlags
  }
)

export const usePermissions = (): ReturnType<typeof selCurrentUserPermissions> => {
  return useSelector(selCurrentUserPermissions)
}
